import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import {
  CheckIcon,
  GKButton,
  GKModal,
  GKPopover,
  Grid2,
  Show,
  Typography,
} from "components";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";
import { useChangeBillingDateMutation } from "store/features/Revenue/FeePlan";
import { GKModalProps } from "utils/commonInterface";

interface Props {
  open: HTMLButtonElement;
  setOpen: (open: any) => void;
  portfolioId: string;
  billingDate: string | null;
}

export const ConfirmationBillingDateModal = (
  props: GKModalProps & {
    selectedBillingDate: string | Dayjs;
    portfolioId: string;
    dateModal: (open: HTMLButtonElement) => void;
  },
) => {
  const { open, setOpen, selectedBillingDate, portfolioId, dateModal } = props;

  const [changeBillingDate, { isLoading }] = useChangeBillingDateMutation();

  return (
    <GKModal
      open={open}
      setOpen={setOpen}
      modalTitle="Edit Billing Date"
      width={"370px"}
      footer={
        <Grid2 size={12} display={"flex"} gap={1}>
          <GKButton
            fullWidth
            variant="outlined"
            color="dark"
            onClick={() => setOpen(false)}
          >
            Cancel
          </GKButton>
          <GKButton
            fullWidth
            variant="contained"
            loading={isLoading}
            startIcon={<CheckIcon />}
            onClick={async () => {
              try {
                await changeBillingDate({
                  portfolioId: portfolioId,
                  startingDate: dayjs(selectedBillingDate).format("YYYY-MM-DD"),
                }).unwrap();
                setOpen(false);
                dateModal(null);
              } catch (error) {
                console.error("Error changing billing date:", error);
              }
            }}
          >
            Yes, Changes Date
          </GKButton>
        </Grid2>
      }
    >
      <Grid2 container spacing={"12px"} mb={"24px"}>
        <Grid2 size={12}>
          <Typography fontSize={12} fontWeight={500} color="var(--neutral-500)">
            Changing the billing date will affect future invoices and payment
            schedules for this client.
          </Typography>
        </Grid2>
        <Grid2 size={12}>
          <Typography fontSize={12} fontWeight={500} color="var(--neutral-500)">
            Are you sure you want to proceed?
          </Typography>
        </Grid2>
      </Grid2>
    </GKModal>
  );
};

const BillingDateModal = (props: Props) => {
  const { open, setOpen, portfolioId, billingDate } = props;

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectBillingDate, setSelectBillingDate] = useState<Dayjs | null>(
    billingDate ? dayjs(billingDate) : null,
  );

  return (
    <>
      <GKPopover
        anchorEl={open}
        open={!!open}
        setOpen={setOpen}
        width={"320px"}
      >
        <Grid2 container>
          <Grid2 size={12}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar
                disableFuture
                value={selectBillingDate}
                onChange={(value) => {
                  setSelectBillingDate(value);
                }}
              />
            </LocalizationProvider>
          </Grid2>
          <Grid2 size={12} display={"flex"} p={"0 16px 16px 16px"} gap={1}>
            <GKButton
              fullWidth
              variant="outlined"
              color="dark"
              onClick={() => setOpen(null)}
            >
              Cancel
            </GKButton>
            <GKButton
              fullWidth
              variant="contained"
              onClick={() => setOpenConfirmModal(true)}
            >
              Apply
            </GKButton>
          </Grid2>
        </Grid2>
      </GKPopover>

      <Show.When isTrue={!!openConfirmModal}>
        <ConfirmationBillingDateModal
          open={openConfirmModal}
          setOpen={setOpenConfirmModal}
          selectedBillingDate={selectBillingDate}
          portfolioId={portfolioId}
          dateModal={setOpen}
        />
      </Show.When>
    </>
  );
};

export default BillingDateModal;
